import React, { useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { useNavigate, Link } from 'react-router-dom';
import { applicationsAPI } from '@/utils/api';
import { useApplicationFocus } from '@/hooks/useApplicationFocus';
import { Card } from '@/components/ui/card';
import { prospectDate } from '@/utils/prospectTime';
import { Button } from '@/components/ui/button';
import {
  Briefcase, Clock, CheckCircle2, XCircle, Eye, MapPin, Calendar,
  Search, FileText, ArrowRight, AlertTriangle, MessageCircle, User
} from 'lucide-react';

const STATUS_ICON = { pending: Clock, reviewed: Eye, accepted: CheckCircle2, rejected: XCircle };
const STATUS_STYLE = {
  pending:   { stripe: 'bg-amber-400',   pill: 'text-amber-800 bg-amber-50 border-amber-200' },
  reviewed:  { stripe: 'bg-blue-500',    pill: 'text-blue-800 bg-blue-50 border-blue-200' },
  accepted:  { stripe: 'bg-emerald-500', pill: 'text-emerald-800 bg-emerald-50 border-emerald-200' },
  rejected:  { stripe: 'bg-slate-400',   pill: 'text-slate-700 bg-slate-50 border-slate-200' }
};

export default function MyApplications() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const focus = useApplicationFocus(items, loading, setFilter);

  useEffect(() => {
    setLoading(true);
    let active = true;
    applicationsAPI.getMy()
      .then(r => { if (active) setItems(r.data); })
      .catch(() => { if (active) setItems([]); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [focus.navigationKey]);

  const counts = useMemo(() => {
    const c = { all: items.length, pending: 0, reviewed: 0, accepted: 0, rejected: 0 };
    items.forEach(i => { if (c[i.status] !== undefined) c[i.status]++; });
    return c;
  }, [items]);

  const filtered = useMemo(
    () => filter === 'all' ? items : items.filter(i => i.status === filter),
    [items, filter]
  );

  const timeAgo = (iso) => {
    if (!iso) return '—';
    try {
      const diff = Date.now() - new Date(iso).getTime();
      const m = Math.floor(diff / 60000);
      if (m < 1) return t('myApplications.timeAgo.now');
      if (m < 60) return t('myApplications.timeAgo.minAgo', { count: m });
      const h = Math.floor(m / 60);
      if (h < 24) return t('myApplications.timeAgo.hoursAgo', { count: h });
      const d = Math.floor(h / 24);
      if (d === 1) return t('myApplications.timeAgo.yesterday');
      if (d < 30) return t('myApplications.timeAgo.daysAgo', { count: d });
      return new Date(iso).toLocaleDateString();
    } catch { return '—'; }
  };

  return (
    <>
      <Helmet>
        <title>{t('myApplications.seoTitle')}</title>
        <meta name="description" content={t('myApplications.seoDescription')} />
        <meta name="robots" content="noindex, nofollow" />
        <link rel="canonical" href="https://trabadju.cv/my-applications" />
      </Helmet>

      <main className="min-h-screen bg-slate-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8">
          <header className="mb-6">
            <p className="text-[11px] font-bold uppercase tracking-widest text-blue-600 mb-1.5">{t('myApplications.eyebrow')}</p>
            <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
              <div>
                <h1 className="text-3xl sm:text-4xl font-bold tracking-tight text-slate-900">{t('myApplications.title')}</h1>
                <p className="text-slate-600 mt-1 text-[14px] sm:text-[15px]">
                  {loading
                    ? t('myApplications.loading')
                    : items.length === 0
                      ? t('myApplications.noneYet')
                      : t(`myApplications.totalCount_${items.length === 1 ? 'one' : 'other'}`, { count: items.length })}
                </p>
              </div>
              {items.length > 0 && (
                <Button onClick={() => navigate('/jobs/search')} className="bg-slate-900 hover:bg-slate-800" data-testid="search-jobs-btn">
                  <Search className="h-4 w-4 mr-2" />{t('myApplications.searchMore')}
                </Button>
              )}
            </div>
          </header>

          {items.length > 0 && (
            <Card className="p-2 mb-5 border-slate-200" data-testid="applications-filter-bar">
              <div className="flex items-center gap-1 overflow-x-auto scrollbar-thin">
                {['all', 'pending', 'reviewed', 'accepted', 'rejected'].map(key => {
                  const active = filter === key;
                  const n = counts[key];
                  return (
                    <button
                      key={key}
                      onClick={() => setFilter(key)}
                      className={`flex-shrink-0 px-3 py-2 rounded-md text-sm font-medium transition-all flex items-center gap-2 ${
                        active ? 'bg-slate-900 text-white shadow-sm' : 'text-slate-600 hover:bg-slate-100'
                      }`}
                      data-testid={`filter-${key}`}
                    >
                      <span>{t(`myApplications.filters.${key}`)}</span>
                      <span className={`inline-flex items-center justify-center text-xs px-1.5 min-w-[20px] h-5 rounded-full font-semibold ${
                        active ? 'bg-white/20 text-white' : 'bg-slate-100 text-slate-700'
                      }`}>{n}</span>
                    </button>
                  );
                })}
              </div>
            </Card>
          )}

          {focus.missing && <p role="status" className="mb-4 rounded-md bg-amber-50 p-3 text-sm text-amber-900" data-testid="notification-application-unavailable">{t('notifications.applicationUnavailable')}</p>}
          {loading ? (
            <div className="text-center py-16 text-slate-500">{t('myApplications.loading')}</div>
          ) : items.length === 0 ? (
            <EmptyState t={t} onSearch={() => navigate('/jobs/search')} onProfile={() => navigate('/candidate/profile')} />
          ) : filtered.length === 0 ? (
            <Card className="p-10 text-center border-dashed border-slate-300">
              <Briefcase className="h-10 w-10 text-slate-400 mx-auto mb-3" />
              <p className="text-slate-800 font-semibold">{t('myApplications.noneInFilter')}</p>
              <p className="text-slate-500 text-sm mt-1">{t('myApplications.tryAnotherFilter')}</p>
            </Card>
          ) : (
            <ul className="space-y-3" data-testid="applications-list">
              {filtered.map(a => <ApplicationCard key={a.id} app={a} focused={a.id === focus.targetId} navigate={navigate} t={t} timeAgo={timeAgo} />)}
            </ul>
          )}

          {!loading && items.length > 0 && (
            <section className="mt-10" aria-labelledby="next-helpers">
              <h2 id="next-helpers" className="sr-only">{t('common.next')}</h2>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <Link to="/jobs/search" className="rounded-xl border border-slate-200 bg-white p-4 hover:border-slate-900 hover:shadow-sm transition group" data-testid="helper-search">
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-lg bg-blue-50 flex items-center justify-center"><Search className="h-4 w-4 text-blue-600" /></div>
                    <div className="min-w-0">
                      <p className="font-semibold text-slate-900 text-[14px] leading-tight">{t('myApplications.helperSearchTitle')}</p>
                      <p className="text-[12.5px] text-slate-500 leading-snug mt-0.5">{t('myApplications.helperSearchBody')}</p>
                    </div>
                    <ArrowRight className="h-4 w-4 text-slate-300 ml-auto group-hover:text-slate-900 transition" />
                  </div>
                </Link>
                <Link to="/candidate/profile" className="rounded-xl border border-slate-200 bg-white p-4 hover:border-slate-900 hover:shadow-sm transition group" data-testid="helper-profile">
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-lg bg-amber-50 flex items-center justify-center"><User className="h-4 w-4 text-amber-600" /></div>
                    <div className="min-w-0">
                      <p className="font-semibold text-slate-900 text-[14px] leading-tight">{t('myApplications.helperProfileTitle')}</p>
                      <p className="text-[12.5px] text-slate-500 leading-snug mt-0.5">{t('myApplications.helperProfileBody')}</p>
                    </div>
                    <ArrowRight className="h-4 w-4 text-slate-300 ml-auto group-hover:text-slate-900 transition" />
                  </div>
                </Link>
                <Link to="/favourites" className="rounded-xl border border-slate-200 bg-white p-4 hover:border-slate-900 hover:shadow-sm transition group" data-testid="helper-favourites">
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-lg bg-rose-50 flex items-center justify-center"><Briefcase className="h-4 w-4 text-rose-600" /></div>
                    <div className="min-w-0">
                      <p className="font-semibold text-slate-900 text-[14px] leading-tight">{t('myApplications.helperFavTitle')}</p>
                      <p className="text-[12.5px] text-slate-500 leading-snug mt-0.5">{t('myApplications.helperFavBody')}</p>
                    </div>
                    <ArrowRight className="h-4 w-4 text-slate-300 ml-auto group-hover:text-slate-900 transition" />
                  </div>
                </Link>
              </div>
            </section>
          )}
        </div>
      </main>
    </>
  );
}

function ApplicationCard({ app, focused, navigate, t, timeAgo }) {
  const status = STATUS_STYLE[app.status] || STATUS_STYLE.pending;
  const StatusIcon = STATUS_ICON[app.status] || Clock;
  const { jobExpired, jobClosed } = useMemo(() => ({
    jobExpired: app.job_status === 'expired' || (app.job_expires_at && new Date(app.job_expires_at).getTime() < Date.now()),
    jobClosed: app.job_status === 'filled' || app.job_status === 'cancelled'
  }), [app.job_status, app.job_expires_at]);
  const emp = t(`myApplications.employment.${(app.job_employment_type || "").replace(/_/g, "-")}`, { defaultValue: app.job_employment_type });

  return (
    <li className="contents">
      <Card id={`application-${app.id}`} tabIndex={-1} className={`relative scroll-mt-24 overflow-hidden border-slate-200 hover:border-slate-300 hover:shadow-md transition-all ${focused ? 'ring-2 ring-blue-500' : ''}`} data-testid={`application-${app.id}`}>
        <div className={`absolute left-0 top-0 bottom-0 w-1 ${status.stripe}`} aria-hidden="true" />
        <div className="p-5 sm:p-6 pl-6">
          <div className="flex items-start justify-between gap-3 mb-2">
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-2 mb-1.5">
                <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-semibold border ${status.pill}`}>
                  <StatusIcon className="h-3 w-3" aria-hidden="true" />{t(`myApplications.status.${app.status}.label`)}
                </span>
                {(jobExpired || jobClosed) && (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[11px] font-semibold border border-slate-200 bg-slate-50 text-slate-600">
                    <AlertTriangle className="h-3 w-3" aria-hidden="true" />{jobClosed ? t('myApplications.jobClosed') : t('myApplications.jobExpired')}
                  </span>
                )}
              </div>
              <h2 className="text-[17px] sm:text-[18px] font-bold text-slate-900 leading-snug truncate" data-testid={`application-title-${app.id}`}>
                {app.job_title || `#${String(app.job_id).slice(0, 8)}…`}
              </h2>
              <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[13px] text-slate-600 mt-1">
                {app.job_company_name && (
                  <span className="font-medium text-slate-800 truncate">{app.job_company_name}</span>
                )}
                {app.job_location && (
                  <span className="inline-flex items-center gap-1">
                    <MapPin className="h-3.5 w-3.5 text-slate-400" aria-hidden="true" />{app.job_location}
                  </span>
                )}
                {emp && (
                  <span className="inline-flex items-center gap-1">
                    <Briefcase className="h-3.5 w-3.5 text-slate-400" aria-hidden="true" />{emp}
                  </span>
                )}
              </div>
            </div>
          </div>

          <p className="text-[13px] text-slate-600 leading-snug mt-3 mb-3">{t(`myApplications.status.${app.status}.micro`)}</p>

          {app.interview && app.interview.status !== 'cancelled' && (
            <div className={`mb-3 rounded-lg border px-3 py-2.5 text-[13px] ${app.interview.status === 'scheduled' ? 'bg-blue-50 border-blue-200 text-blue-900' : app.interview.status === 'completed' ? 'bg-emerald-50 border-emerald-200 text-emerald-900' : 'bg-amber-50 border-amber-200 text-amber-900'}`} data-testid={`application-interview-${app.id}`}>
              {app.interview.status === 'proposed' && (
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <span className="font-semibold inline-flex items-center gap-1.5"><Calendar className="h-3.5 w-3.5" />{t('myApplications.interview.proposed')}</span>
                  <Button size="sm" className="bg-amber-500 hover:bg-amber-600 text-[#0A2342] h-8" onClick={() => navigate(`/entrevista/${app.interview.token}`)} data-testid={`pick-slot-${app.id}`}>{t('myApplications.interview.pick')}</Button>
                </div>
              )}
              {app.interview.status === 'scheduled' && (
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <span className="font-semibold inline-flex items-center gap-1.5"><Calendar className="h-3.5 w-3.5" />{t('myApplications.interview.scheduled', { when: `${prospectDate(app.interview.scheduled_start).toLocaleString('pt-PT', { weekday: 'short', day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit', timeZone: 'Atlantic/Cape_Verde' })} (hora de Cabo Verde)` })}</span>
                  {app.interview.video?.join_url && (
                    <a href={app.interview.video.join_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 rounded-md bg-blue-600 text-white px-2.5 py-1 text-xs font-semibold hover:bg-blue-700" data-testid={`join-interview-${app.id}`}>{t('myApplications.interview.join')}</a>
                  )}
                </div>
              )}
              {app.interview.status === 'completed' && <span className="font-semibold">{t(`myApplications.interview.outcome.${app.interview.outcome}`)}</span>}
              {app.interview.status === 'needs_reschedule' && <span className="font-semibold">{t('myApplications.interview.needsReschedule')}</span>}
            </div>
          )}

          {app.message && (
            <details className="mb-3 group">
              <summary className="cursor-pointer text-[12px] text-slate-500 hover:text-slate-700 inline-flex items-center gap-1.5 select-none">
                <MessageCircle className="h-3.5 w-3.5" aria-hidden="true" />{t('myApplications.yourMessage')}
              </summary>
              <p className="mt-2 text-[13px] text-slate-700 bg-slate-50 border border-slate-100 px-3 py-2 rounded-md italic leading-snug">
                &ldquo;{app.message}&rdquo;
              </p>
            </details>
          )}

          <div className="flex items-center justify-between gap-3 pt-3 border-t border-slate-100">
            <span className="text-[12px] text-slate-500 inline-flex items-center gap-1.5">
              <Calendar className="h-3.5 w-3.5" aria-hidden="true" />
              {t('myApplications.sentAgo', { time: timeAgo(app.applied_at) })}
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate(`/jobs/${app.job_id}`)}
              className="border-slate-300"
              data-testid={`view-job-${app.id}`}
            >
              {t('myApplications.viewJob')}<ArrowRight className="h-3.5 w-3.5 ml-1" aria-hidden="true" />
            </Button>
          </div>
        </div>
      </Card>
    </li>
  );
}

function EmptyState({ t, onSearch, onProfile }) {
  return (
    <Card className="p-8 sm:p-10 border-slate-200 text-center" data-testid="applications-empty-state">
      <div className="w-14 h-14 rounded-full bg-blue-50 flex items-center justify-center mx-auto mb-4">
        <FileText className="h-7 w-7 text-blue-600" aria-hidden="true" />
      </div>
      <h2 className="text-[20px] sm:text-[22px] font-bold text-slate-900 mb-2">{t('myApplications.emptyTitle')}</h2>
      <p className="text-slate-600 text-[14px] sm:text-[15px] max-w-md mx-auto mb-6 leading-relaxed">
        {t('myApplications.emptyBody')}
      </p>
      <div className="flex flex-col sm:flex-row gap-2 justify-center max-w-md mx-auto">
        <Button onClick={onSearch} className="bg-slate-900 hover:bg-slate-800 flex-1" data-testid="empty-search-btn">
          <Search className="h-4 w-4 mr-2" />{t('myApplications.emptySearch')}
        </Button>
        <Button variant="outline" onClick={onProfile} className="border-slate-300 flex-1" data-testid="empty-profile-btn">
          <User className="h-4 w-4 mr-2" />{t('myApplications.emptyProfile')}
        </Button>
      </div>
      <div className="mt-8 pt-6 border-t border-slate-100 grid grid-cols-1 sm:grid-cols-3 gap-3 text-[12.5px] text-slate-600">
        <div className="flex items-center justify-center gap-2"><CheckCircle2 className="h-4 w-4 text-emerald-600" />{t('myApplications.trustFree')}</div>
        <div className="flex items-center justify-center gap-2"><Eye className="h-4 w-4 text-blue-600" />{t('myApplications.trustDirect')}</div>
        <div className="flex items-center justify-center gap-2"><Clock className="h-4 w-4 text-amber-600" />{t('myApplications.trustFast')}</div>
      </div>
    </Card>
  );
}
